/**
 * @module engine/input/ScrollbarController
 * @summary Drag + track-click behaviour for the custom scrollbars.
 * @description Operates on the engine-owned scrollbar DOM (rendered by React,
 *   queried by id). Thumb geometry is painted each frame by render.updateScrollbars.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3599-3644.
 */
import { S, markDirty } from '../state';
import { getWorldBounds } from '../render';

/** Attach scrollbar drag/click handlers. Returns a detach fn. */
export function attachScrollbarControls(): () => void {
  const hThumb = document.getElementById('h-scrollbar-thumb');
  const vThumb = document.getElementById('v-scrollbar-thumb');
  const hBar = document.getElementById('h-scrollbar');
  const vBar = document.getElementById('v-scrollbar');

  let hDrag = false;
  let vDrag = false;
  let hStart = { mx: 0, px: 0 };
  let vStart = { my: 0, py: 0 };

  const onHThumbDown = (e: MouseEvent) => {
    hDrag = true;
    hStart = { mx: e.clientX, px: S.panX };
    e.stopPropagation();
    e.preventDefault();
  };
  const onVThumbDown = (e: MouseEvent) => {
    vDrag = true;
    vStart = { my: e.clientY, py: S.panY };
    e.stopPropagation();
    e.preventDefault();
  };
  const onDocMove = (e: MouseEvent) => {
    if (hDrag && hBar) {
      const wb = getWorldBounds();
      const hTk = hBar.clientWidth;
      const vpW = S.CW / S.zoom;
      const hTW = Math.max(24, hTk * Math.min(1, vpW / wb.w));
      const dx = e.clientX - hStart.mx;
      const worldDx = dx * (wb.w / (hTk - hTW));
      S.panX = -((-hStart.px / S.zoom + worldDx) * S.zoom);
      markDirty();
    }
    if (vDrag && vBar) {
      const wb = getWorldBounds();
      const vTk = vBar.clientHeight;
      const vpH = S.CH / S.zoom;
      const vTH = Math.max(24, vTk * Math.min(1, vpH / wb.h));
      const dy = e.clientY - vStart.my;
      const worldDy = dy * (wb.h / (vTk - vTH));
      S.panY = -((-vStart.py / S.zoom + worldDy) * S.zoom);
      markDirty();
    }
  };
  const onDocUp = () => {
    hDrag = false;
    vDrag = false;
  };
  const onHBarClick = (e: MouseEvent) => {
    if (e.target === hThumb || !hBar) return;
    const rect = hBar.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const wb = getWorldBounds();
    const hTk = hBar.clientWidth;
    const vpW = S.CW / S.zoom;
    const hTW = Math.max(24, hTk * Math.min(1, vpW / wb.w));
    const prog = (x - hTW / 2) / (hTk - hTW);
    const vpX = wb.x + Math.max(0, Math.min(1, prog)) * (wb.w - vpW);
    S.panX = -(vpX * S.zoom);
    markDirty();
  };
  const onVBarClick = (e: MouseEvent) => {
    if (e.target === vThumb || !vBar) return;
    const rect = vBar.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const wb = getWorldBounds();
    const vTk = vBar.clientHeight;
    const vpH = S.CH / S.zoom;
    const vTH = Math.max(24, vTk * Math.min(1, vpH / wb.h));
    const prog = (y - vTH / 2) / (vTk - vTH);
    const vpY = wb.y + Math.max(0, Math.min(1, prog)) * (wb.h - vpH);
    S.panY = -(vpY * S.zoom);
    markDirty();
  };

  hThumb?.addEventListener('mousedown', onHThumbDown);
  vThumb?.addEventListener('mousedown', onVThumbDown);
  document.addEventListener('mousemove', onDocMove);
  document.addEventListener('mouseup', onDocUp);
  hBar?.addEventListener('click', onHBarClick);
  vBar?.addEventListener('click', onVBarClick);

  return () => {
    hThumb?.removeEventListener('mousedown', onHThumbDown);
    vThumb?.removeEventListener('mousedown', onVThumbDown);
    document.removeEventListener('mousemove', onDocMove);
    document.removeEventListener('mouseup', onDocUp);
    hBar?.removeEventListener('click', onHBarClick);
    vBar?.removeEventListener('click', onVBarClick);
  };
}
